// App Creator AI Inference System - Extends AIInferenceSystem for app creation workflow
// Handles the landing page AI interaction for generating app metadata from user ideas

import { AIInferenceSystem } from "../../../base/AIInferenceSystem.js";
import { logger } from "../../../../../core/logger.js";

export class AppCreatorAIInferenceSystem extends AIInferenceSystem {
  constructor() {
    super();
    this.appCreatorManager = null; // Reference to AppCreatorManager for navigation
  }

  /**
   * Get the instance identifier for app creator AI inference
   * @returns {string} "app-creator"
   */
  getInstanceId() {
    return "app-creator";
  }

  /**
   * Set app creator manager reference for navigation
   * @param {AppCreatorManager} appCreatorManager - The AppCreatorManager instance
   */
  setAppCreatorManager(appCreatorManager) {
    this.appCreatorManager = appCreatorManager;
  }

  /**
   * Initialize chat with app creation system prompt
   * This should be called when setting up the landing page session
   */
  initializeChat() {
    try {
      // Clear existing chat
      this.clearChat();

      // Add app creation system message
      const systemPrompt = `You are an AI assistant that helps users create app projects.

When a user describes an app idea, respond with a JSON object containing exactly these 4 required fields:

1. "title" - A catchy, descriptive app title (3-8 words, under 50 characters)
2. "description" - Clear description of what the app does (2-3 sentences)
3. "color" - Hex color code representing the app's theme (#RRGGBB format, like "#FF6B35")
4. "startingMessage" - Detailed instructions for developers to build the app (3-5 sentences)

Example response:
{
  "title": "Basketball Score Tracker",
  "description": "Track basketball game scores, player statistics, and team performance. Features include live score updates, player stats tracking, and game history storage.",
  "color": "#FF6B35",
  "startingMessage": "Create a comprehensive basketball tracking application that allows users to record and monitor game scores in real-time. Include features for tracking individual player statistics such as points scored, rebounds, assists, steals, blocks, and fouls. Provide team performance analytics across multiple games with historical data storage and visualization."
}

Important: Always provide valid values for all 4 fields in the exact JSON format shown.
- Description should explain the app's main functionality (under 200 characters)
- Starting message should be clear, detailed instructions for developers (under 500 characters)
- Starting message should expand on the user's request with specific features and requirements
- Color should be a valid 6-character hex code (like "#FF6B35", "#4A90E2", "#50C878")
- Color should reflect the app's theme - e.g., blue for productivity, green for finance, red for social, etc.
- Always respond with valid JSON only, no additional text`;

      this.chat.addMessage({
        role: "system",
        content: systemPrompt,
      });

      logger.log(
        {
          tags: "app-creator-ai-inference|chat-init|success",
          color1: "green",
        },
        "AppCreator chat initialized successfully"
      );
    } catch (error) {
      logger.error(
        {
          tags: "app-creator-ai-inference|chat-init|error",
          color1: "red",
        },
        "Failed to initialize AppCreator chat:",
        error
      );
      throw error;
    }
  }

  /**
   * Process user input and generate app metadata using structured outputs
   * @param {string} userMessage - User's app idea description
   * @param {string} userId - User ID for authentication
   * @returns {Promise<Object>} App metadata object with title, description, startingMessage
   */
  async processAppIdea(userMessage, userId) {
    try {
      logger.log(
        {
          tags: "app-creator-ai-inference|process|start",
          color1: "cyan",
        },
        "Processing app idea with structured outputs:",
        userMessage
      );

      // Add user message to chat
      this.chat.addMessage({
        role: "user",
        content: userMessage,
      });

      // Process through AI inference system with structured outputs
      await this.infer("meta-llama/llama-3.1-8b-instruct", userId, {
        requestOptions: {
          response_format: {
            type: "json_schema",
            json_schema: {
              name: "app_metadata",
              strict: true,
              schema: {
                type: "object",
                properties: {
                  title: {
                    type: "string",
                    description: "App title (3-8 words, catchy)",
                  },
                  description: {
                    type: "string",
                    description: "App description (2-3 sentences)",
                  },
                  startingMessage: {
                    type: "string",
                    description: "Developer instructions (3-5 sentences)",
                  },
                  color: {
                    type: "string",
                    description:
                      "Hex color code representing the app (6 characters, format: #RRGGBB)",
                    pattern: "^#[0-9A-Fa-f]{6}$",
                  },
                },
                required: ["title", "description", "startingMessage", "color"],
                additionalProperties: false,
              },
            },
          },
        },
      });

      // Extract the final AI response
      const messages = this.chat.getAllMessages();
      const lastAssistantMessage = messages
        .filter((msg) => msg.data.role === "assistant")
        .pop();

      if (!lastAssistantMessage) {
        throw new Error("No AI response generated");
      }

      // Extract content from segments - structured outputs guarantee valid JSON
      const content =
        lastAssistantMessage.data.segments
          ?.map((segment) => segment.content)
          .join("") ||
        lastAssistantMessage.data.content ||
        "";

      logger.log(
        {
          tags: "app-creator-ai-inference|process|response",
          color1: "lime",
        },
        "AI structured response received:",
        content
      );

      // Parse JSON directly - no regex needed with structured outputs
      const parsedResponse = JSON.parse(content);

      // Set defaults for missing or invalid fields (graceful fallback instead of error)
      const defaultsUsed = [];
      const usedDefaults = {};

      // Helper function to get valid field value or default
      const getFieldValue = (fieldName, value, defaultValue, validator = null) => {
        const isValid = value &&
                       typeof value === "string" &&
                       value.trim().length > 0 &&
                       (!validator || validator(value.trim()));

        if (!isValid) {
          defaultsUsed.push(fieldName);
          usedDefaults[fieldName] = defaultValue;
          return defaultValue;
        }
        return value.trim();
      };

      const title = getFieldValue("title", parsedResponse.title, "My App");
      const description = getFieldValue("description", parsedResponse.description,
        "A custom app created with AI assistance");
      const startingMessage = getFieldValue("startingMessage", parsedResponse.startingMessage,
        "Create an amazing app! Add your code and features here.");
      const color = getFieldValue("color", parsedResponse.color, "#3B82F6",
        (val) => /^#[0-9A-Fa-f]{6}$/.test(val));

      // Log when defaults are used
      if (defaultsUsed.length > 0) {
        logger.warn(
          {
            tags: "app-creator-ai-inference|process|defaults",
            color1: "yellow",
          },
          `AI response missing/invalid fields, using defaults: ${defaultsUsed.join(", ")}`,
          usedDefaults
        );
      }

      const result = {
        title,
        description,
        startingMessage,
        color,
        originalMessage: userMessage,
      };

      logger.log(
        {
          tags: "app-creator-ai-inference|process|success",
          color1: "lime",
        },
        `App idea processed successfully${defaultsUsed.length > 0 ? ` (used defaults for: ${defaultsUsed.join(", ")})` : ""}:`,
        result
      );

      return result;
    } catch (error) {
      logger.error(
        {
          tags: "app-creator-ai-inference|process|error",
          color1: "red",
        },
        "Failed to process app idea:",
        error
      );
      throw error;
    }
  }

  /**
   * Clean up app creator-specific resources
   */
  destroy() {
    // Clean up app creator-specific references
    this.appCreatorManager = null;
    super.destroy();
  }
}
